import React from 'react'
import { DcButton, DcFlexBox, DcOverlaySlider, DcTag, DcText } from 'pixel-base'
import { CloseIcon } from 'utils/icons/close-icon'
import { TrashIcon } from 'utils/icons/trash-icon'

interface ValidationResultItem {
  product_id?: number
  product_sku: string
  product_name?: string
  product_image_path?: string
  status: 'success' | 'error'
  message: string
  error_code?: string
  details?: any
}

interface ValidationResults {
  data: ValidationResultItem[]
  valid_products: any[]
  _meta: {
    total_skus: number
    found_count: number
    error_count: number
    success_rate: number
  }
}

interface ValidationResultsSliderProps {
  isOpen: boolean
  onClose: () => void
  validationResults: ValidationResults | null
  onRemoveFailedProducts?: () => void
}

const ValidationResultsSlider: React.FC<ValidationResultsSliderProps> = ({
  isOpen,
  onClose,
  validationResults,
  onRemoveFailedProducts,
}) => {
  if (!validationResults) return null

  return (
    <DcOverlaySlider
      isOpen={isOpen}
      onClose={onClose}
      title="Product Validation Results"
      width="80%"
    >
      <DcFlexBox
        height="auto"
        flexDirection="column"
        gap="20px"
        style={{ padding: '20px' }}
      >
        {/* Summary Section */}
        <DcFlexBox
          height="auto"
          style={{
            background: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}
        >
          <DcFlexBox height="auto" flexDirection="column" gap="12px">
            <DcText
              textSize="18px"
              style={{ fontWeight: '600', color: '#495057' }}
            >
              Validation Summary
            </DcText>
            <DcFlexBox gap="40px">
              <DcFlexBox flexDirection="column" alignItems="center">
                <DcText
                  textSize="24px"
                  style={{ fontWeight: '600', color: '#495057' }}
                >
                  {validationResults._meta.total_skus}
                </DcText>
                <DcText textSize="12px" style={{ color: '#6c757d' }}>
                  Total Products
                </DcText>
              </DcFlexBox>
              <DcFlexBox flexDirection="column" alignItems="center">
                <DcText
                  textSize="24px"
                  style={{ fontWeight: '600', color: '#28a745' }}
                >
                  {validationResults._meta.found_count}
                </DcText>
                <DcText textSize="12px" style={{ color: '#28a745' }}>
                  Valid Products
                </DcText>
              </DcFlexBox>
              <DcFlexBox flexDirection="column" alignItems="center">
                <DcText
                  textSize="24px"
                  style={{ fontWeight: '600', color: '#dc3545' }}
                >
                  {validationResults._meta.error_count}
                </DcText>
                <DcText textSize="12px" style={{ color: '#dc3545' }}>
                  Failed Products
                </DcText>
              </DcFlexBox>
              <DcFlexBox flexDirection="column" alignItems="center">
                <DcText
                  textSize="24px"
                  style={{ fontWeight: '600', color: '#007bff' }}
                >
                  {validationResults._meta.success_rate}%
                </DcText>
                <DcText textSize="12px" style={{ color: '#007bff' }}>
                  Success Rate
                </DcText>
              </DcFlexBox>
            </DcFlexBox>
          </DcFlexBox>
        </DcFlexBox>

        {/* Product Details */}
        <DcFlexBox height="auto" flexDirection="column" gap="12px">
          <DcText
            textSize="16px"
            style={{ fontWeight: '600', color: '#495057' }}
          >
            Product Details ({validationResults.data.length})
          </DcText>
          <div style={{ maxHeight: '650px', overflowY: 'auto' }}>
            {validationResults.data.map((item, index) => (
              <DcFlexBox
                key={index}
                style={{
                  background: '#ffffff',
                  padding: '16px',
                  borderRadius: '8px',
                  border: `1px solid ${
                    item.status === 'success' ? '#d4edda' : '#f8d7da'
                  }`,
                  marginBottom: '12px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}
                flexDirection="column"
                gap="12px"
              >
                {/* Product Header */}
                <DcFlexBox justifyContent="space-between" alignItems="center">
                  <DcFlexBox gap="12px" alignItems="center">
                    {item.product_image_path && (
                      <img
                        src={item.product_image_path}
                        alt={item.product_name || item.product_sku}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '4px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                    <DcFlexBox flexDirection="column" gap="4px">
                      <DcText
                        textSize="14px"
                        style={{ fontWeight: '500', color: '#495057' }}
                      >
                        {item.product_name || item.product_sku}
                      </DcText>
                      <DcText textSize="12px" style={{ color: '#6c757d' }}>
                        SKU: {item.product_sku}
                      </DcText>
                    </DcFlexBox>
                  </DcFlexBox>
                  <DcTag
                    style={{
                      backgroundColor:
                        item.status === 'success' ? '#d4edda' : '#f8d7da',
                      color: item.status === 'success' ? '#155724' : '#721c24',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                    }}
                  >
                    {item.status === 'success' ? 'VALID' : 'FAILED'}
                  </DcTag>
                </DcFlexBox>

                {/* Status Message */}
                <DcFlexBox
                  style={{
                    background:
                      item.status === 'success' ? '#d4edda' : '#f8d7da',
                    padding: '12px',
                    borderRadius: '6px',
                    border: `1px solid ${
                      item.status === 'success' ? '#c3e6cb' : '#f5c6cb'
                    }`,
                  }}
                >
                  <DcText
                    textSize="14px"
                    style={{
                      color: item.status === 'success' ? '#155724' : '#721c24',
                    }}
                  >
                    {item.message}
                  </DcText>
                </DcFlexBox>

                {/* Error Details */}
                {item.status === 'error' && item.details && (
                  <DcFlexBox
                    style={{
                      background: '#fff3cd',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #ffeaa7',
                    }}
                    flexDirection="column"
                    gap="8px"
                  >
                    <DcText
                      textSize="12px"
                      style={{ fontWeight: '600', color: '#856404' }}
                    >
                      Error Details:
                    </DcText>
                    {item.error_code === 'PRODUCT_NOT_IN_WAREHOUSE' && (
                      <DcFlexBox flexDirection="column" gap="4px">
                        <DcText textSize="12px" style={{ color: '#856404' }}>
                          • Available Quantity:{' '}
                          {item.details.available_quantity}
                        </DcText>
                        <DcText textSize="12px" style={{ color: '#856404' }}>
                          • Requested Quantity:{' '}
                          {item.details.requested_quantity}
                        </DcText>
                        <DcText textSize="12px" style={{ color: '#856404' }}>
                          • Warehouse ID: {item.details.warehouse_id}
                        </DcText>
                      </DcFlexBox>
                    )}
                    {item.error_code === 'PRODUCT_NOT_FOUND' && (
                      <DcText textSize="12px" style={{ color: '#856404' }}>
                        • SKU &quot;{item.details.sku}&quot; was not found in
                        the system
                      </DcText>
                    )}
                  </DcFlexBox>
                )}
              </DcFlexBox>
            ))}
          </div>
        </DcFlexBox>

        {/* Action Buttons */}
        <DcFlexBox height="auto" justifyContent="flex-end" gap="12px">
          <DcButton
            size="sm"
            variant="outline-secondary"
            onClick={onClose}
            withIcon={true}
          >
            <CloseIcon size={16} />
            <span>Close</span>
          </DcButton>
          {validationResults._meta.error_count > 0 &&
            onRemoveFailedProducts && (
              <DcButton
                size="sm"
                variant="outline-danger"
                onClick={onRemoveFailedProducts}
                withIcon={true}
              >
                <TrashIcon size={16} />
                <span>Remove Failed Products</span>
              </DcButton>
            )}
        </DcFlexBox>
      </DcFlexBox>
    </DcOverlaySlider>
  )
}

export default ValidationResultsSlider
